import React, { useState, useEffect } from "react";
import { Box, Grid, Typography, CircularProgress } from "@mui/material";
import { getSnapshotViewData } from "../../../../apis/plans.apis";

const placeholderData = {
  visibilityZone: {
    front: 72,
    rear: 28,
  },
};

const formatMetricValue = (value) => {
  // 1. If value is null or undefined, return '0'
  if (value === null || typeof value === "undefined") {
    return "0";
  }
  // 2. If it's already a string with units (e.g., "30 sec"), return it as is
  if (typeof value === "string" && isNaN(parseFloat(value))) {
    return value;
  }
  // 3. Format large numbers
  const num = Number(value);
  if (num >= 1_000_000_000) return `${(num / 1_000_000_000).toFixed(1)} B`;
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)} M`;
  if (num >= 1_000) return `${(num / 1_000).toFixed(1)} K`;

  return String(value); // Return the original number as a string if it's small
};

const categories = ["Promising", "Good", "Excellent", "Outstanding"];

const getRatingDetails = (value) => {
  if (value >= 0 && value <= 25) return { name: "Promising", color: "#ffc107" };
  if (value > 25 && value <= 50) return { name: "Good", color: "#21bff3ff" };
  if (value > 50 && value <= 75)
    return { name: "Excellent", color: " #8bc34a" };
  if (value > 75 && value <= 100)
    return { name: "Outstanding", color: "#4caf50" };
  return { name: "", color: "grey.300" };
};

const AttentionScoreBar = ({ label, value }) => {
  const rating = getRatingDetails(value);
  return (
    <Box sx={{ mb: 3, width: "100%" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="caption" sx={{ fontWeight: "bold" }}>
          {rating.name} - {value}
        </Typography>
      </Box>
      <Box sx={{ position: "relative", width: "100%", height: "40px" }}>
        <Box
          sx={{
            display: "flex",
            width: "100%",
            height: "100%",
            position: "absolute",
          }}
        >
          {categories.map((cat) => (
            <Box
              key={cat}
              sx={{
                flex: 1,
                border: "1px solid #e0e0e0",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#f5f5f5",
              }}
            >
              <Typography variant="caption" color="text.secondary">
                {cat}
              </Typography>
            </Box>
          ))}
        </Box>
        <Box
          sx={{
            position: "absolute",
            height: "100%",
            width: `${value}%`,
            backgroundColor: rating.color,
            transition: "width 0.5s ease-in-out",
            opacity: 0.8,
          }}
        />
      </Box>
    </Box>
  );
};

const MetricItem = ({ label, value }) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1.5 }}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: "bold" }}>
      {formatMetricValue(value)}
    </Typography>
  </Box>
);

// --- Main Snapshot Component ---
const SnapshotView = ({ billboardId }) => {
  // 1. State for loading, data, and errors
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // 2. useEffect to fetch data from the backend
  useEffect(() => {
    const fetchData = async () => {
      if (!billboardId) {
        setLoading(false);
        setError("Billboard ID is missing.");
        return;
      }
      try {
        setLoading(true);
        const responseData = await getSnapshotViewData(billboardId);
        setData(responseData);
      } catch (err) {
        setError("Failed to load snapshot data.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [billboardId]);

  // 3. Conditional rendering for loading and error states
  if (loading) {
    return (
      <Box
        sx={{
          p: 5,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "50vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }
  if (error) {
    return (
      <Box
        sx={{
          p: 5,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "50vh",
        }}
      >
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4, backgroundColor: "#fff" }}>
      <Grid container spacing={5}>
        {/* Column 1 */}
        <Grid item xs={12} md={6}>
          <Box>
            <Typography variant="h6" gutterBottom>
              Attention Score
            </Typography>
            {/* 4. Use live 'data' for most sections */}
            {data.attentionScore?.map((item) => (
              <AttentionScoreBar
                key={item.label}
                label={item.label}
                value={item.value}
              />
            ))}
          </Box>
          <Box>
            <Typography variant="h6" gutterBottom>
              Impression Score
            </Typography>
            {/* 4. Use live 'data' for most sections */}
            {data.impressionScore?.map((item) => (
              <AttentionScoreBar
                key={item.label}
                label={item.label}
                value={item.value}
              />
            ))}
          </Box>
        </Grid>

        {/* Column 2 */}
        <Grid item xs={12} md={6}>
          <Box>
            <Typography variant="h6" gutterBottom>
              Visibility Duration
            </Typography>
            {data.visibilityDuration?.map((item) => (
              <MetricItem key={item.label} {...item} />
            ))}
          </Box>
          <Box>
            <Typography variant="h6" gutterBottom>
              Average Speed
            </Typography>
            {data.averageSpeed?.map((item) => (
              <MetricItem key={item.label} {...item} />
            ))}
          </Box>
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>
              Impressions
            </Typography>
            {data.impressions?.map((item) => (
              <MetricItem key={item.label} {...item} />
            ))}
          </Box>
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>
              Visibility Zone Split
            </Typography>
            {/* 5. Use hardcoded 'placeholderData' for this section */}
            <MetricItem
              label="Front Visibility"
              value={`${data.visibilityZoneSplit?.front}%`}
            />
            <MetricItem
              label="Rear Visibility"
              value={`${data.visibilityZoneSplit?.rear}%`}
            />
            <Box
              sx={{
                display: "flex",
                height: 10,
                borderRadius: 1,
                overflow: "hidden",
                mt: 1,
              }}
            >
              <Box
                sx={{
                  width: `${data.visibilityZoneSplit?.front}%`,
                  backgroundColor: "primary.main",
                }}
              />
              <Box
                sx={{
                  width: `${data.visibilityZoneSplit?.rear}%`,
                  backgroundColor: "grey.300",
                }}
              />
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SnapshotView;
